/**
 * aileSwitcherService.js — Backend API versiyonu
 * Firestore tamamen kaldırıldı. Çoklu aile işlemleri PostgreSQL/backend üzerinden yürür.
 */
import { apiRequest } from "./apiClient";
import { defaultPermissions, adminPermissions } from "../utils/permissions";
import { rt } from "../i18n/runtime";
import { joinFamilyWithInvite } from "./familyInviteService";

const normalizeFamily = (family = {}) => ({
  familyId: family.familyId || family.id || "",
  pgFamilyId: family.pgFamilyId || family.pg_family_id || family.databaseFamilyId || "",
  name: family.familyName || family.name || "",
  code: family.familyCode || family.code || "",
  ownerUid: family.ownerUid || "",
  ikon: family.ikon || family.icon || "🏠",
  renk: family.renk || family.color || "#6366f1",
  rol: family.rol || family.role || "Aile Üyesi",
  permissions: family.permissions || ((family.rol || family.role) === "Aile Yöneticisi" ? adminPermissions : defaultPermissions),
  joinedAt: family.joinedAt || family.createdAt || null,
});

const familyLimitKontrol = (families) => {
  if ((families || []).length >= 10) {
    throw new Error(rt("service.error.familyLimit"));
  }
};

/* ── Kullanıcının tüm aile bilgilerini çek ── */
export const kullaniciFamilileriniGetir = async () => {
  const data = await apiRequest("/families/my");
  return (data.families || []).map(normalizeFamily);
};

/* ── Yeni aile oluştur (çoklu aile desteğiyle) ── */
export const yeniAileOlusturService = async ({ aileAdi, ikon, renk, user }) => {
  const mevcut = await kullaniciFamilileriniGetir().catch(() => []);
  familyLimitKontrol(mevcut);

  // NOT: `permissions` gövdeye EKLENMEZ. Backend, aileyi oluşturan kullanıcıya
  // "Aile Yöneticisi" rolünü ve tam yetkiyi zaten sunucu tarafında atar.
  // Ayrıca adminPermissions objesi içinde `health`/`children` gibi sayfa-izni
  // boolean anahtarları bulunur; bunlar Android Lite gövde denetimine yanlış
  // pozitif olarak takılıp aile oluşturmayı 400 ile engelliyordu.
  const data = await apiRequest("/families/create", {
    method: "POST",
    body: JSON.stringify({
      name: aileAdi.trim(),
      ikon: ikon || "🏠",
      renk: renk || "#6366f1",
    }),
  });

  const f = normalizeFamily(data.family || {});
  try { localStorage.setItem("fp:aktivFamilyId", f.familyId); } catch {}

  return {
    familyId: f.familyId,
    familyName: f.name,
    familyCode: f.code,
    ownerUid: f.ownerUid || user?.uid || "",
    rol: "Aile Yöneticisi",
    permissions: adminPermissions,
    ikon: f.ikon || ikon || "🏠",
    renk: f.renk || renk || "#6366f1",
  };
};

/* ── Kodla aileye katıl ── */
export const kodlaAileyeKatilService = async ({ katilmaKodu }) => {
  const mevcut = await kullaniciFamilileriniGetir().catch(() => []);
  familyLimitKontrol(mevcut);

  // NOT: `permissions` gövdeye EKLENMEZ. Backend, katılan kullanıcıya varsayılan
  // üye yetkisini sunucu tarafında atar. defaultPermissions objesi içinde
  // `health`/`children` sayfa-izni boolean anahtarları bulunduğundan, bu gövde
  // Android Lite denetimine yanlış pozitif takılıp katılmayı engelleyebiliyordu.
  const data = await joinFamilyWithInvite({ code: katilmaKodu });

  const f = normalizeFamily(data.family || {});
  if (!f.familyId) throw new Error(rt("familyInvite.errors.invalid"));
  try { localStorage.setItem("fp:aktivFamilyId", f.familyId); } catch {}

  return {
    familyId: f.familyId,
    familyName: f.name,
    familyCode: "",
    ownerUid: f.ownerUid || "",
    rol: f.rol || "Aile Üyesi",
    permissions: f.permissions || defaultPermissions,
    ikon: f.ikon || "🏠",
    renk: f.renk || "#10b981",
  };
};

/* ── Aktif aileyi değiştir ── */
export const aktifAileDegistirService = async ({ familyId, families, currentFamilyId }) => {
  const hedefMeta = families?.find((f) => f.familyId === familyId || f.id === familyId);
  const yeniRol = hedefMeta?.rol || hedefMeta?.role || "Aile Üyesi";
  const yeniPermissions = hedefMeta?.permissions || (yeniRol === "Aile Yöneticisi" ? adminPermissions : defaultPermissions);

  const data = await apiRequest("/families/active", {
    method: "POST",
    body: JSON.stringify({ familyId, previousFamilyId: currentFamilyId || null }),
  });

  const f = normalizeFamily(data.family || hedefMeta || { familyId, rol: yeniRol, permissions: yeniPermissions });
  try { localStorage.setItem("fp:aktivFamilyId", f.familyId || familyId); } catch {}

  return {
    rol: f.rol || yeniRol,
    permissions: f.permissions || yeniPermissions,
  };
};
